import { Button, Col, Form, Row, Badge } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { Typography } from "../../components/utilities/Typhograpy";
import InfoIcon from "../../components/utilities/InfoIcon";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faEdit,
  faBriefcase,
  faClock,
  faFileInvoiceDollar,
  faMoneyBillWave,
} from "@fortawesome/free-solid-svg-icons";
import { FormProvider, useForm } from "react-hook-form";
import { useContext, useEffect, useMemo, useState } from "react";
import GlobalContext from "../../context/GlobalContext";
import swal from "../../services/swalConfig";
import { editUser, getUserInfo } from "../../api/UsersApi";
import BoxSkeleton from "../../components/utilities/BoxSkeleton";
import { format } from "date-fns";
import BackButton from "../../components/button/BackButton";
import DepartementSelect from "../../components/DepartementSelect";
import TimeZoneSelect from "../../components/TimeZoneSelect";
import SelectPayrollTemplate from "../../components/select/SelectPayrollTemplate";
import SelectPersonnel from "../../components/select/SelectPersonnel";

import { tr } from "../../i18n";
const UsersEdit = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, setLoading } = useContext(GlobalContext);
  const personnelId = location.state?.personnel_id || null;
  const [personelData, setPersonelData] = useState(null);
  const methods = useForm();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setError,
  } = methods;

  const payrollSchemaLabel = useMemo(() => {
    if (!personelData?.internal_payroll_schema) {
      return "No payroll schema assigned";
    }

    try {
      const parsedSchema =
        typeof personelData.internal_payroll_schema === "string"
          ? JSON.parse(personelData.internal_payroll_schema)
          : personelData.internal_payroll_schema;

      return parsedSchema?.settings?.template_name || "Custom Schema";
    } catch (error) {
      return "Custom Schema";
    }
  }, [personelData?.internal_payroll_schema]);

  const payrollTemplateMeta = useMemo(() => {
    if (!personelData?.internal_payroll_schema) {
      return { templateId: null, templateName: null };
    }

    try {
      const parsedSchema =
        typeof personelData.internal_payroll_schema === "string"
          ? JSON.parse(personelData.internal_payroll_schema)
          : personelData.internal_payroll_schema;

      return {
        templateId: parsedSchema?.settings?.template_id || null,
        templateName: parsedSchema?.settings?.template_name || null,
      };
    } catch (error) {
      return { templateId: null, templateName: null };
    }
  }, [personelData?.internal_payroll_schema]);

  useEffect(() => {
    let isActive = true;

    const loadPersonnelInformation = async () => {
      setPersonelData(null);

      try {
        const response = await getUserInfo(personnelId);

        if (!isActive) {
          return;
        }

        if (response.status === 200) {
          setPersonelData(response.data);

          let selectedPayrollTemplateId = "";
          try {
            const parsedSchema =
              typeof response.data.internal_payroll_schema === "string"
                ? JSON.parse(response.data.internal_payroll_schema)
                : response.data.internal_payroll_schema;

            selectedPayrollTemplateId = parsedSchema?.settings?.template_id
              ? String(parsedSchema.settings.template_id)
              : "";
          } catch (error) {
            selectedPayrollTemplateId = "";
          }

          reset({
            internal_id: response.data.internal_id || "",
            email: response.data.internal_email || "",
            department: response.data.internal_department || "",
            join_date:
              response.data.internal_join_date &&
              response.data.internal_join_date !== "0000-00-00 00:00:00"
                ? format(
                    new Date(response.data.internal_join_date),
                    "yyyy-MM-dd",
                  )
                : "",
            timeZone: response.data.internal_time_zone || "",
            supervisor: response.data.supervisor_id || null,
            payroll_template_id: selectedPayrollTemplateId,
          });
        } else {
          setPersonelData({});
        }
      } catch (error) {
        if (isActive) {
          setPersonelData({});
        }
      }
    };

    if (personnelId && user?.workspace?.company_id) {
      loadPersonnelInformation();
    }

    return () => {
      isActive = false;
    };
  }, [personnelId, reset, user]);

  return (
    <Row>
      <Col xs={12}>
        <div className="box-shadow p-4 bg-white rounded-4">
          {personelData === null ? (
            <BoxSkeleton count="6" size="12" height="20px" />
          ) : (
            <FormProvider {...methods}>
              <Form
                onSubmit={handleSubmit(async (data) => {
                  setLoading(true);

                  try {
                    const updatedData = {
                      ...data,
                      personnel_id: personnelId,
                      company_id: user.workspace.company_id,
                    };

                    const response = await editUser(updatedData);

                    if (response.status === 400) {
                      Object.entries(response.errors).forEach(
                        ([key, message]) =>
                          setError(key, { type: "manual", message }),
                      );

                      return;
                    }

                    await swal.fire({
                      title:
                        response.status === 200 ? tr("users:congratulations") : tr("common:errors.oops"),
                      text: response.message,
                      icon: response.status === 200 ? "success" : "error",
                      showCancelButton: false,
                    });
                  } catch (error) {
                    swal.fire({
                      title: tr("common:errors.oops"),
                      text: tr("users:something_wrong_try_logging_in_later"),
                      icon: "error",
                      showCancelButton: false,
                    });
                  } finally {
                    setLoading(false);
                  }
                })}
              >
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
                  <div>
                    <Typography variant="body2" fontWeight={600}>
                      {tr("users:edit_employee_workspace_profile")}
                    </Typography>
                    <Typography variant="caption" style={{ opacity: 0.75 }}>
                      {tr("users:keep_personal_profile_as_reference_then")}
                    </Typography>
                  </div>
                  <Badge bg="light" text="dark" className="px-3 py-2 border">
                    {tr("users:personnel_id")}{" "} {personelData.internal_id || "-"}
                  </Badge>
                </div>

                <div
                  className="border rounded-4 p-4 mb-4"
                  style={{
                    background:
                      "linear-gradient(180deg, #fbfcff 0%, #f5f8fc 100%)",
                  }}
                >
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <Typography variant="body2" fontWeight={600}>
                      {tr("users:personal_information")}
                    </Typography>
                    <InfoIcon info="This is a brief summary of the user's personal information, editable only by the user." />
                  </div>
                  <Row className="g-3">
                    <Col md={6}>
                      <div className="border rounded-3 p-3 h-100 bg-white">
                        <div className="text-muted small mb-1">{tr("users:full_name")}</div>
                        <div className="fw-semibold">
                          {personelData.personal_name || "-"}
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="border rounded-3 p-3 h-100 bg-white">
                        <div className="text-muted small mb-1">
                          {tr("users:personal_email")}
                        </div>
                        <div className="fw-semibold">
                          {personelData.personal_email || "-"}
                        </div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="border rounded-3 p-3 h-100 bg-white">
                        <div className="text-muted small mb-1">{tr("users:birthday")}</div>
                        <div className="fw-semibold">
                          {personelData.personal_birthday &&
                          !isNaN(Date.parse(personelData.personal_birthday))
                            ? format(
                                new Date(personelData.personal_birthday),
                                "d MMMM yyyy",
                              )
                            : "-"}
                        </div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="border rounded-3 p-3 h-100 bg-white">
                        <div className="text-muted small mb-1">{tr("shared:country")}</div>
                        <div className="fw-semibold">
                          {personelData.personal_country || "-"}
                        </div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="border rounded-3 p-3 h-100 bg-white">
                        <div className="text-muted small mb-1">
                          {tr("users:province_city")}
                        </div>
                        <div className="fw-semibold">
                          {[
                            personelData.personal_province,
                            personelData.personal_city,
                          ]
                            .filter(Boolean)
                            .join(" / ") || "-"}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>

                <div
                  className="border rounded-4 p-4"
                  style={{ background: "#fff" }}
                >
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <Typography variant="body2" fontWeight={600}>
                      {tr("users:organization_information")}
                    </Typography>
                    <InfoIcon info="This is the user's internal organization info, which the organization can modify as needed." />
                  </div>

                  <Row className="g-4">
                    <Col lg={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <Typography variant="form">{tr("shared:internal_id")}</Typography>
                        </Form.Label>
                        <Form.Control
                          {...register("internal_id")}
                          type="text"
                          placeholder={tr("shared:internal_id")}
                        />
                        {errors.internal_id ? (
                          <small className="text-danger">
                            {errors.internal_id.message}
                          </small>
                        ) : null}
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>
                          <Typography variant="form">{tr("users:internal_email_2")}</Typography>
                        </Form.Label>
                        <Form.Control
                          {...register("email", {
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: tr("users:invalid_email_address"),
                            },
                          })}
                          type="text"
                          placeholder={tr("users:internal_email")}
                        />
                        {errors.email ? (
                          <small className="text-danger">
                            {errors.email.message}
                          </small>
                        ) : null}
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>
                          <Typography variant="form">{tr("shared:department")}</Typography>
                        </Form.Label>
                        <DepartementSelect
                          name="department"
                          defaultValue={
                            personelData.internal_department
                              ? {
                                  value: personelData.internal_department,
                                  label: personelData.internal_department,
                                }
                              : null
                          }
                        />
                        {errors.department ? (
                          <small className="text-danger">
                            {errors.department.message}
                          </small>
                        ) : null}
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>
                          <Typography variant="form">{tr("users:supervisor")}</Typography>
                        </Form.Label>
                        <SelectPersonnel
                          name="supervisor"
                          defaultValue={personelData.supervisor_id || null}
                          placeholder={tr("users:choose_supervisor")}
                        />
                        {personelData.supervisor_full_name ? (
                          <Typography
                            variant="caption"
                            style={{ opacity: 0.75 }}
                          >
                            {tr("users:current_supervisor")}{" "}
                            {personelData.supervisor_full_name}
                          </Typography>
                        ) : null}
                        {errors.supervisor ? (
                          <small className="text-danger d-block">
                            {errors.supervisor.message}
                          </small>
                        ) : null}
                      </Form.Group>
                    </Col>

                    <Col lg={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <Typography variant="form">{tr("shared:join_date")}</Typography>
                        </Form.Label>
                        <Form.Control {...register("join_date")} type="date" />
                        {errors.join_date ? (
                          <small className="text-danger">
                            {errors.join_date.message}
                          </small>
                        ) : null}
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>
                          <Typography variant="form">{tr("shared:time_zone")}</Typography>
                        </Form.Label>
                        <TimeZoneSelect
                          name="timeZone"
                          defaultValue={
                            personelData.internal_time_zone
                              ? {
                                  id: personelData.internal_time_zone,
                                  text: personelData.internal_time_zone,
                                }
                              : null
                          }
                        />
                        {errors.timeZone ? (
                          <small className="text-danger">
                            {errors.timeZone.message}
                          </small>
                        ) : null}
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>
                          <Typography variant="form">{tr("shared:payroll_schema")}</Typography>
                        </Form.Label>
                        <SelectPayrollTemplate
                          name="payroll_template_id"
                          placeholder={tr("users:choose_payroll_schema_to_replace_current")}
                          defaultValue={
                            payrollTemplateMeta.templateId
                              ? {
                                  value: String(payrollTemplateMeta.templateId),
                                  label: payrollTemplateMeta.templateName
                                    ? `${payrollTemplateMeta.templateId} - ${payrollTemplateMeta.templateName}`
                                    : String(payrollTemplateMeta.templateId),
                                }
                              : null
                          }
                        />
                        <div className="d-flex flex-wrap gap-2 mt-2">
                          <Badge bg="light" text="dark" className="border">
                            {tr("shared:schema")}{" "} {payrollSchemaLabel}
                          </Badge>
                          <Badge
                            bg={
                              payrollTemplateMeta.templateId
                                ? "success"
                                : "warning"
                            }
                            text={
                              payrollTemplateMeta.templateId ? "light" : "dark"
                            }
                            className="border"
                          >
                            {payrollTemplateMeta.templateId
                              ? tr("users:template_schema")
                              : tr("users:custom_schema")}
                          </Badge>
                        </div>
                        <Button
                          type="button"
                          variant="outline-primary"
                          className="d-flex align-items-center gap-2 mt-3"
                          onClick={() => {
                            navigate(`/payroll/form?personnel=${personnelId}`);
                          }}
                        >
                          <FontAwesomeIcon icon={faEdit} /> {" "}{tr("users:edit_payroll_schema")}
                        </Button>
                        {errors.payroll_template_id ? (
                          <small className="text-danger d-block">
                            {errors.payroll_template_id.message}
                          </small>
                        ) : null}
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="g-3 mt-1 mb-4">
                    <Col md={4}>
                      <div
                        className="border rounded-3 p-3 h-100"
                        style={{ background: "#fafbff" }}
                      >
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <FontAwesomeIcon
                            icon={faMoneyBillWave}
                            className="text-primary"
                          />
                          <Typography variant="form">{tr("common:breadcrumb.salary")}</Typography>
                        </div>
                        <div className="fw-semibold mb-2">
                          {(personelData.internal_sallary_currency_symbol ||
                            "Rp") +
                            " " +
                            Number(
                              personelData.internal_sallary_amount || 0,
                            ).toLocaleString("id-ID")}
                        </div>
                        <Button
                          variant="light"
                          className="border"
                          onClick={() => {
                            navigate("salary", {
                              state: { personnel_id: personnelId },
                            });
                          }}
                        >
                          <FontAwesomeIcon icon={faEdit} /> {" "}{tr("users:edit_salary")}
                        </Button>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div
                        className="border rounded-3 p-3 h-100"
                        style={{ background: "#fafbff" }}
                      >
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <FontAwesomeIcon
                            icon={faBriefcase}
                            className="text-primary"
                          />
                          <Typography variant="form">{tr("common:breadcrumb.paid-leave")}</Typography>
                        </div>
                        <div className="fw-semibold mb-2">
                          {personelData.internal_paid_leave || 0} {" "}{tr("shared:days")}
                        </div>
                        <Button
                          variant="light"
                          className="border"
                          onClick={() => {
                            navigate("paid-leave", {
                              state: { personnel_id: personnelId },
                            });
                          }}
                        >
                          <FontAwesomeIcon icon={faEdit} /> {" "}{tr("users:edit_paid_leave")}
                        </Button>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div
                        className="border rounded-3 p-3 h-100"
                        style={{ background: "#fafbff" }}
                      >
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <FontAwesomeIcon
                            icon={faClock}
                            className="text-primary"
                          />
                          <Typography variant="form">{tr("common:breadcrumb.schedule")}</Typography>
                        </div>
                        <div className="fw-semibold mb-2">
                          {personelData.schedules?.length || 0} {" "}{tr("users:configured_days")}
                        </div>
                        <Button
                          variant="light"
                          className="border"
                          onClick={() => {
                            navigate("schedule", {
                              state: { personnel_id: personnelId },
                            });
                          }}
                        >
                          <FontAwesomeIcon icon={faEdit} /> {" "}{tr("users:edit_schedule")}
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  <div
                    className="border rounded-3 p-3 mb-4"
                    style={{ background: "#f8fbff" }}
                  >
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <FontAwesomeIcon
                        icon={faFileInvoiceDollar}
                        className="text-primary"
                      />
                      <Typography variant="form">{tr("users:quick_summary")}</Typography>
                    </div>
                    <div className="small text-muted">
                      {tr("users:use_this_page_for_general_personnel")}
                    </div>
                  </div>

                  <div className="mt-3 d-flex justify-content-between">
                    <BackButton />
                    <Button type="submit" className="btn btn-primary">
                      <FontAwesomeIcon icon={faCheckCircle} /> {" "}{tr("users:update_information")}
                    </Button>
                  </div>
                </div>
              </Form>
            </FormProvider>
          )}
        </div>
      </Col>
    </Row>
  );
};

export default UsersEdit;
